import { getCadenceBucketKey } from "./assignment";
import { getExperimentByKey } from "./registry";
import { safeLocalStorage } from "../../utils/safeStorage";

const STORAGE_KEY = "experiments:active:v1";
const MAX_ACTIVE_EXPERIMENTS = 50;
const DEFAULT_TZ = "America/Los_Angeles";

const safeParse = (value) => {
  try {
    return JSON.parse(value);
  } catch (error) {
    return null;
  }
};

const readStore = () => {
  if (typeof window === "undefined") return {};

  try {
    const raw = safeLocalStorage.getItem(STORAGE_KEY);
    const parsed = raw ? safeParse(raw) : null;
    const normalized =
      parsed && typeof parsed === "object" ? normalizeStore(parsed) : {};

    if (raw && JSON.stringify(normalized) !== raw) {
      writeStore(normalized);
    }

    return normalized;
  } catch (_error) {
    return {};
  }
};

const writeStore = (store) => {
  if (typeof window === "undefined") return;

  try {
    safeLocalStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch (_error) {}
};

const pruneStore = (store = {}) => {
  const entries = Object.entries(store);
  if (entries.length <= MAX_ACTIVE_EXPERIMENTS) {
    return store;
  }

  entries.sort(([, left], [, right]) => {
    const leftTs = Date.parse(left?.updatedAt || left?.assignedAt || 0) || 0;
    const rightTs = Date.parse(right?.updatedAt || right?.assignedAt || 0) || 0;
    return leftTs - rightTs;
  });

  const next = { ...store };
  const deleteCount = entries.length - MAX_ACTIVE_EXPERIMENTS;
  for (let index = 0; index < deleteCount; index += 1) {
    delete next[entries[index][0]];
  }
  return next;
};

const normalizeCadence = (value = "") =>
  String(value || "")
    .trim()
    .toLowerCase();

const buildStoreEntryKey = ({
  experimentKey = "",
  subjectId = "",
  subjectScope = "",
} = {}) =>
  [
    String(subjectScope || "").trim().toLowerCase(),
    String(subjectId || "").trim(),
    String(experimentKey || "").trim(),
  ]
    .filter(Boolean)
    .join("|");

const isRotatingAssignmentContextActive = (context = {}) => {
  if (String(context?.source || "").trim().toLowerCase() !== "assignment") {
    return true;
  }

  const cadence = normalizeCadence(context?.metadata?.cadence || "static");
  if (!cadence || cadence === "static") {
    return true;
  }

  const storedBucketKey = String(context?.metadata?.bucketKey || "").trim();
  if (!storedBucketKey) {
    return false;
  }

  const timeZone =
    String(context?.metadata?.timeZone || "").trim() || DEFAULT_TZ;

  return getCadenceBucketKey(cadence, undefined, timeZone) === storedBucketKey;
};

const isRegisteredExperimentContextActive = (context = {}) => {
  const experimentKey = String(context?.experimentKey || "").trim();
  if (!experimentKey) return false;
  return getExperimentByKey(experimentKey)?.status === "active";
};

const normalizeStore = (store = {}) =>
  pruneStore(
    Object.fromEntries(
      Object.entries(store).filter(([, context]) => {
        if (!context || typeof context !== "object") return false;
        if (!String(context?.experimentKey || "").trim()) return false;
        if (!String(context?.variantKey || "").trim()) return false;
        if (!String(context?.subjectId || "").trim()) return false;
        if (!String(context?.subjectScope || "").trim()) return false;
        if (!isRegisteredExperimentContextActive(context)) return false;
        return isRotatingAssignmentContextActive(context);
      }),
    ),
  );

const filterStoreBySubject = (
  store = {},
  { userId = "", sessionId = "" } = {},
) => {
  const resolvedUserId = String(userId || "").trim();
  const resolvedSessionId = String(sessionId || "").trim();

  return Object.fromEntries(
    Object.entries(store).filter(([, context]) => {
      const subjectId = String(context?.subjectId || "").trim();
      const subjectScope = String(context?.subjectScope || "")
        .trim()
        .toLowerCase();

      if (!subjectId) {
        return false;
      }

      if (subjectScope === "user" || subjectScope === "member") {
        return Boolean(resolvedUserId) && subjectId === resolvedUserId;
      }

      if (subjectScope === "session") {
        return Boolean(resolvedSessionId) && subjectId === resolvedSessionId;
      }

      return (
        (resolvedUserId && subjectId === resolvedUserId) ||
        (resolvedSessionId && subjectId === resolvedSessionId)
      );
    }),
  );
};

const getContextPriority = (context = {}) => {
  const subjectScope = String(context?.subjectScope || "")
    .trim()
    .toLowerCase();

  if (subjectScope === "user") return 2;
  if (subjectScope === "session") return 1;
  return 0;
};

const collapseStoreByExperiment = (store = {}) =>
  Object.values(store).reduce((accumulator, context) => {
    const experimentKey = String(context?.experimentKey || "").trim();
    if (!experimentKey) {
      return accumulator;
    }

    const existing = accumulator[experimentKey];
    if (!existing) {
      accumulator[experimentKey] = context;
      return accumulator;
    }

    const nextPriority = getContextPriority(context);
    const existingPriority = getContextPriority(existing);
    const nextUpdatedAt = Date.parse(context?.updatedAt || 0) || 0;
    const existingUpdatedAt = Date.parse(existing?.updatedAt || 0) || 0;

    if (
      nextPriority > existingPriority ||
      (nextPriority === existingPriority && nextUpdatedAt >= existingUpdatedAt)
    ) {
      accumulator[experimentKey] = context;
    }

    return accumulator;
  }, {});

export const getActiveExperimentContexts = (options = {}) =>
  collapseStoreByExperiment(filterStoreBySubject(readStore(), options));

export const getActiveExperimentContext = (experimentKey = "", options = {}) =>
  getActiveExperimentContexts(options)[String(experimentKey || "").trim()] || null;

export const rememberExperimentContext = ({
  experimentKey = "",
  variantKey = "",
  source = "assignment",
  surface = "",
  subjectId = "",
  subjectScope = "",
  metadata = {},
} = {}) => {
  const resolvedExperimentKey = String(experimentKey || "").trim();
  const resolvedVariantKey = String(variantKey || "").trim();
  const resolvedSubjectId = String(subjectId || "").trim();
  const resolvedSubjectScope = String(subjectScope || "")
    .trim()
    .toLowerCase();

  if (
    !resolvedExperimentKey ||
    !resolvedVariantKey ||
    !resolvedSubjectId ||
    !resolvedSubjectScope
  ) {
    return null;
  }

  const now = new Date().toISOString();
  const store = readStore();
  const entryKey = buildStoreEntryKey({
    experimentKey: resolvedExperimentKey,
    subjectId: resolvedSubjectId,
    subjectScope: resolvedSubjectScope,
  });
  const existing = store?.[entryKey] || {};
  const existingRevisionId = String(
    existing?.metadata?.revisionId || "",
  ).trim();
  const nextRevisionId = String(metadata?.revisionId || "").trim();
  const revisionChanged =
    Boolean(nextRevisionId) && nextRevisionId !== existingRevisionId;
  const next = normalizeStore({
    ...store,
    [entryKey]: {
      experimentKey: resolvedExperimentKey,
      variantKey: resolvedVariantKey,
      source: String(source || "assignment"),
      surface: String(surface || ""),
      subjectId: resolvedSubjectId,
      subjectScope: resolvedSubjectScope,
      assignedAt: revisionChanged ? now : existing.assignedAt || now,
      updatedAt: now,
      metadata: metadata && typeof metadata === "object" ? metadata : {},
    },
  });

  writeStore(next);
  return next[entryKey] || null;
};

export const clearExperimentContext = (
  experimentKey = "",
  { subjectId = "", subjectScope = "" } = {},
) => {
  const resolvedExperimentKey = String(experimentKey || "").trim();
  if (!resolvedExperimentKey) return;

  const store = readStore();
  const resolvedSubjectId = String(subjectId || "").trim();
  const resolvedSubjectScope = String(subjectScope || "")
    .trim()
    .toLowerCase();

  const next = Object.fromEntries(
    Object.entries(store).filter(([, context]) => {
      const sameExperiment =
        String(context?.experimentKey || "").trim() === resolvedExperimentKey;
      if (!sameExperiment) return true;

      if (!resolvedSubjectId || !resolvedSubjectScope) {
        return false;
      }

      return !(
        String(context?.subjectId || "").trim() === resolvedSubjectId &&
        String(context?.subjectScope || "").trim().toLowerCase() ===
          resolvedSubjectScope
      );
    }),
  );

  if (Object.keys(next).length === Object.keys(store).length) return;
  writeStore(next);
};
